import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { stockDatasets } from '../data/sampleData';

interface DatasetSelectorProps {
  onSelectionChange: (selectedDatasets: string[]) => void;
  initialSelection?: string[];
  selectedDatasets?: string[];
}

const AVAILABLE_DATASETS = stockDatasets.map(dataset => ({
  value: dataset.id,
  label: `${dataset.name} (${dataset.symbol})`
}));

const DatasetSelector = ({ onSelectionChange, initialSelection = [], selectedDatasets: externalSelectedDatasets }: DatasetSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(externalSelectedDatasets || initialSelection);

  useEffect(() => {
    if (externalSelectedDatasets) {
      setSelectedValues(externalSelectedDatasets);
    } else if (initialSelection.length > 0) {
      setSelectedValues(initialSelection);
    } else if (selectedValues.length === 0 && AVAILABLE_DATASETS.length > 0) {
      setSelectedValues([AVAILABLE_DATASETS[0].value]);
      onSelectionChange([AVAILABLE_DATASETS[0].value]);
    }
  }, [initialSelection, externalSelectedDatasets]);

  const handleSelect = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    
    if (newSelectedValues.length === 0) {
      return;
    }
    
    setSelectedValues(newSelectedValues);
    onSelectionChange(newSelectedValues);
  };

  const handleClearAll = () => {
    const firstSelected = selectedValues.length > 0 ? [selectedValues[0]] : [AVAILABLE_DATASETS[0].value];
    setSelectedValues(firstSelected);
    onSelectionChange(firstSelected);
  };

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedValues.length === 0
              ? "Select datasets..."
              : `${selectedValues.length} dataset${selectedValues.length > 1 ? 's' : ''} selected`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search datasets..." />
            <div className="px-3 py-2 border-b">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-muted-foreground"
                onClick={handleClearAll}
              >
                Clear all
              </Button>
            </div>
            <CommandList>
              <CommandEmpty>No datasets found.</CommandEmpty>
              <CommandGroup>
                {AVAILABLE_DATASETS.map((dataset) => (
                  <CommandItem
                    key={dataset.value}
                    value={dataset.value}
                    onSelect={() => handleSelect(dataset.value)}
                  >
                    <div className="flex items-center w-full">
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedValues.includes(dataset.value) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex-1">{dataset.label}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {selectedValues.map((value) => {
            const dataset = AVAILABLE_DATASETS.find(d => d.value === value);
            return (
              <div 
                key={value} 
                className="bg-primary/10 text-primary text-xs px-3 py-1 rounded-full"
              >
                {dataset?.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DatasetSelector;
